import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { money, readableErrors, reconcile } from './invoiceStatus';

function Field({ label, value, testid, mono = false }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-overline text-ink-muted">{label}</dt>
      <dd className={`text-ink mt-0.5 ${mono ? 'font-mono' : 'font-medium'}`} data-testid={testid}>{value || '—'}</dd>
    </div>
  );
}

export function AddsUpCheck({ invoice }) {
  const r = reconcile(invoice);
  if (r.addsUp === null) return null;
  if (r.addsUp) {
    return (
      <div className="flex items-start gap-2 rounded-xl bg-primary-50 border border-primary-200 px-4 py-3 text-primary-700" data-testid="adds-up-ok">
        <CheckCircle2 className="w-5 h-5 shrink-0" aria-hidden="true" />
        <span className="text-sm"><strong>Adds up.</strong> Lines {money(r.net)} + VAT {money(r.vat)} = total {money(r.total)}.</span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 rounded-xl bg-accent-50 border border-accent-300 px-4 py-3 text-accent-800" data-testid="adds-up-warning">
      <AlertTriangle className="w-5 h-5 shrink-0" aria-hidden="true" />
      <span className="text-sm"><strong>Does not add up.</strong> Lines {money(r.net)} + VAT {money(r.vat)} = {money(r.expected)}, but the total reads {money(r.total)} ({money(Math.abs(r.gap))} {r.gap > 0 ? 'more' : 'less'}). Check the lines and the total against the document.</span>
    </div>
  );
}

/** VAT by code: the document's printed analysis beside what the lines add to, so a wrong code shows up as a gap. */
export function VatBreakdown({ invoice }) {
  const lines = invoice.lines || [];
  const sum = (code, key) => Math.round(lines.filter((l) => String(l.vat_code ?? '') === String(code))
    .reduce((s, l) => s + (Number(l[key]) || 0), 0) * 100) / 100;
  // Codes the invoice prints with nothing on them (Holland's 2, 3 and 4) add no information: hide them.
  const printed = (invoice.vat_analysis || []).filter((r) =>
    Number(r.net) || Number(r.vat) || sum(r.code, 'line_total') || sum(r.code, 'vat_amount'));
  if (!printed.length) return null;
  return (
    <table className="w-full text-sm" data-testid="vat-breakdown">
      <thead className="text-xs uppercase tracking-overline text-ink-muted text-left">
        <tr><th className="py-1">VAT code</th><th>Rate</th><th className="text-right">Goods (lines)</th><th className="text-right">Goods (printed)</th><th className="text-right">VAT (lines)</th><th className="text-right">VAT (printed)</th></tr>
      </thead>
      <tbody className="font-mono">
        {printed.map((r) => (
          <tr key={r.code} data-testid={`vat-row-${r.code}`}>
            <td className="py-1">{r.code}</td><td>{Number(r.rate_percent)}%</td>
            <td className="text-right">{money(sum(r.code, 'line_total'))}</td><td className="text-right">{money(r.net)}</td>
            <td className="text-right">{money(sum(r.code, 'vat_amount'))}</td><td className="text-right">{money(r.vat)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function InvoiceSummary({ invoice }) {
  const r = reconcile(invoice);
  const errs = invoice.extraction_errors || [];
  return (
    <section className="rounded-2xl border border-surface-border bg-surface p-5 space-y-4" data-testid="invoice-summary">
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="col-span-2 sm:col-span-3"><Field label="Supplier" value={invoice.supplier_name} testid="field-supplier" /></div>
        <Field label="Invoice number" value={invoice.invoice_no} testid="field-invoice-no" />
        <Field label="Date" value={invoice.date} testid="field-date" />
        <Field label="Net (sum of lines)" value={money(r.net)} testid="field-net" mono />
        <Field label="VAT" value={money(invoice.totals?.vat_total)} testid="field-vat" mono />
        <Field label="Total" value={money(invoice.totals?.invoice_total)} testid="field-total" mono />
      </dl>
      <AddsUpCheck invoice={invoice} />
      <VatBreakdown invoice={invoice} />
      {errs.length > 0 && (
        <p className="text-sm text-accent-800" data-testid="extraction-errors">Could not read everything: {readableErrors(errs)}.</p>
      )}
    </section>
  );
}
